import { useCallback, useEffect, useMemo, useState } from "react";

import {
  UsersService,
  WorkspacesService,
  type AddMemberRequest,
  type UserOut,
  type WorkspaceMembership,
  type WorkspaceRole,
} from "@/api";
import { useApi, useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { cn } from "@/lib/utils";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Props {
  workspaceId: string | null;
  existingMemberIds: ReadonlyArray<string>;
  onClose: () => void;
  onAdded: (membership: WorkspaceMembership) => void;
}

// Role identifiers paired with their label + help translation keys.
// Built into the option list inside the component via ``useMemo`` so
// it re-renders on a locale switch.
const ROLE_OPTION_KEYS: ReadonlyArray<{
  value: WorkspaceRole;
  labelKey: TranslationKey;
  helpKey: TranslationKey;
}> = [
  {
    value: "workspace_admin",
    labelKey: "workspaces.roles.workspace_admin",
    helpKey: "workspaces.addMemberDialog.roleHintAdmin",
  },
  {
    value: "workspace_member",
    labelKey: "workspaces.roles.workspace_member",
    helpKey: "workspaces.addMemberDialog.roleHintMember",
  },
  {
    value: "workspace_viewer",
    labelKey: "workspaces.roles.workspace_viewer",
    helpKey: "workspaces.addMemberDialog.roleHintViewer",
  },
];

/** Pick a tenant user + assign workspace role. Pulls the tenant
 *  user list once when the dialog opens (paginated; admin role
 *  gated server-side). Already-members are filtered out so the
 *  picker only shows valid candidates. */
export function AddMemberDialog({
  workspaceId,
  existingMemberIds,
  onClose,
  onAdded,
}: Props) {
  const { t } = useTranslation();
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [role, setRole] = useState<WorkspaceRole>("workspace_member");
  const [search, setSearch] = useState("");

  const roleOptions = useMemo(
    () =>
      ROLE_OPTION_KEYS.map((opt) => ({
        value: opt.value,
        label: t(opt.labelKey),
        help: t(opt.helpKey),
      })),
    [t]
  );

  const userFetcher = useCallback(
    () => UsersService.list({ limit: 200 }),
    []
  );
  const usersQuery = useApi(userFetcher, { enabled: workspaceId !== null });

  const mutator = useCallback(
    async ({ id, body }: { id: string; body: AddMemberRequest }) =>
      WorkspacesService.addMember(id, body),
    []
  );
  const { mutate, isLoading, error, reset } = useMutation<
    { id: string; body: AddMemberRequest },
    WorkspaceMembership
  >(mutator, {
    onSuccess: (m) => {
      onAdded(m);
      setSelectedUserId(null);
      setRole("workspace_member");
      setSearch("");
    },
  });

  // Reset transient state whenever the dialog opens fresh.
  useEffect(() => {
    if (!workspaceId) {
      setSelectedUserId(null);
      setRole("workspace_member");
      setSearch("");
      reset();
    }
  }, [workspaceId, reset]);

  if (!workspaceId) return null;

  const existing = new Set(existingMemberIds);
  const allUsers = usersQuery.data?.items ?? [];
  const candidates = allUsers
    .filter((u) => !existing.has(u.id) && u.status === "active")
    .filter((u) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        u.email.toLowerCase().includes(q) ||
        (u.full_name ?? "").toLowerCase().includes(q)
      );
    })
    .slice(0, 50);

  const canSubmit = selectedUserId !== null && !isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !selectedUserId) return;
    await mutate({
      id: workspaceId,
      body: { user_id: selectedUserId, role },
    });
  };

  const handleClose = () => {
    reset();
    setSelectedUserId(null);
    setRole("workspace_member");
    setSearch("");
    onClose();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-lg">
      <DialogHeader>
        <DialogTitle>{t("workspaces.addMemberDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("workspaces.addMemberDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div>
            <label
              htmlFor="member-search"
              className="label-eyebrow mb-1 block"
            >
              {t("workspaces.addMemberDialog.searchLabel")}
            </label>
            <input
              id="member-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("workspaces.addMemberDialog.searchPlaceholder")}
              className={cn(
                "h-10 w-full rounded-md border border-ocean-100 bg-white px-3 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-ocean-400"
              )}
            />
          </div>

          <div className="max-h-56 overflow-y-auto rounded-md border border-ocean-100 bg-white">
            {usersQuery.isLoading && (
              <div className="px-3 py-2 text-xs text-neutral-500">
                {t("workspaces.addMemberDialog.loadingUsers")}
              </div>
            )}
            {!usersQuery.isLoading && candidates.length === 0 && (
              <div className="px-3 py-3 text-xs text-neutral-500">
                {t("workspaces.addMemberDialog.noCandidates")}
              </div>
            )}
            {candidates.map((u: UserOut) => (
              <label
                key={u.id}
                className={cn(
                  "flex cursor-pointer items-start gap-3 border-b border-ocean-100 px-3 py-2 last:border-b-0 hover:bg-ocean-50",
                  selectedUserId === u.id && "bg-ocean-50"
                )}
              >
                <input
                  type="radio"
                  name="member-user"
                  value={u.id}
                  checked={selectedUserId === u.id}
                  onChange={() => setSelectedUserId(u.id)}
                  className="mt-1 h-4 w-4 border-ocean-200 text-ocean-600 focus:ring-ocean-400"
                />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ocean-900">
                    {u.full_name || u.email}
                  </div>
                  <div className="truncate text-xs text-neutral-600">
                    {u.email}
                  </div>
                </div>
                <span className="shrink-0 text-xs text-neutral-500">
                  {u.role}
                </span>
              </label>
            ))}
          </div>

          <div>
            <div className="label-eyebrow mb-2">
              {t("workspaces.addMemberDialog.roleLabel")}
            </div>
            <div
              role="radiogroup"
              aria-label={t("workspaces.addMemberDialog.roleGroupAriaLabel")}
              className="space-y-2"
            >
              {roleOptions.map((opt) => {
                const checked = opt.value === role;
                return (
                  <label
                    key={opt.value}
                    className={cn(
                      "flex cursor-pointer items-start gap-3 rounded-md border px-3 py-2 text-sm transition-colors",
                      checked
                        ? "border-ocean-300 bg-ocean-50"
                        : "border-ocean-100 hover:border-ocean-200"
                    )}
                  >
                    <input
                      type="radio"
                      name="member-role"
                      value={opt.value}
                      checked={checked}
                      onChange={() => setRole(opt.value)}
                      className="mt-0.5 h-4 w-4 border-ocean-200 text-ocean-600 focus:ring-ocean-400"
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-ocean-900">
                        {opt.label}
                      </div>
                      <div className="mt-0.5 text-xs text-neutral-600">
                        {opt.help}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("workspaces.addMemberDialog.cancel")}
          </Button>
          <Button type="submit" disabled={!canSubmit}>
            {isLoading
              ? t("workspaces.addMemberDialog.submitLoading")
              : t("workspaces.addMemberDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
